import { useMemo, useEffect, useCallback, useState, Dispatch, SetStateAction, ChangeEvent } from 'react'

import { useAppDispatch, useAppSelector } from 'redux/toolkit/hooks'
import { AvailableSettings, SettingValue } from 'types/Settings'

import {
  getAccountSettings,
  getDomainSettings,
  resetAccountAndDomainSettings,
  updateAccountSettings,
  updateDomainSettings
} from 'redux/features/settings/settingsSlice'
import { isSuccess } from 'redux/toolkit/api'
import { RadioOptions } from 'components/libs/settings/SettingsRowRadio'
import {
  AVAILABLE_CONTENT_INTENT_ACTIONS,
  AVAILABLE_CPL_CONTENT_INTENT_ACTIONS,
  AntiPhishingSettings,
  BafiActions,
  ContentIntentActions,
  EmailBannersActions
} from 'types/AntiPhishing'
import { useDirtyFormObjectCheck } from 'lib/useDirtyFormObjectCheck'
import { useInboundSettingsRights } from 'components/libs/userRights/pages/useInboundSettingsRights'
import { useDomainPolicyAccess } from 'lib/hooks/useDomainPolicyAccess'
import { isEmailValid } from 'lib/validation'
import { useFormatMessage } from 'lib/localization'
import { isMyFeatureOn } from 'lib/splitio'
import { FEATURES } from 'lib/splitioFeatures'
import appConfig from 'config/appConfig'
import routesConfig from 'lib/routesConfig'
import { TRACKING_EVENTS, trackIntercomEventDelayed } from 'lib/monitoring/intercom/intercomService'

export interface State {
  isCplAccount?: boolean
  bafiOptions: RadioOptions[]
  contentIntentActions: string[]
  form: AntiPhishingSettings
  isDisabledSubmit: boolean
  isDisabledNavigation: boolean
  error: string
  isEmailBannerEnabled: boolean
  showEmailBannersHelp: boolean
  isDomainLevel: boolean
  hasNoDomainPolicyAccess: boolean
  isWarningBannerExemptionsOn: boolean
  isAntifraudExemptionsOn: boolean
}

export interface EventHandlers {
  onBulkEdit: () => void
  onFormChange: (name: AvailableSettings) => (NewValue: any) => void
  onSave: () => void
  onCancelConfirm: () => void
  helpConfig: {
    isOpen: boolean
    onHelpClick: () => void
    onCloseHelp: () => void
  }
  setIsAntiPhishingTableDirty: Dispatch<SetStateAction<boolean>>
  setIsWarningBannerExemptTableDirty: Dispatch<SetStateAction<boolean>>
  setIsAntifraudExemptTableDirty: Dispatch<SetStateAction<boolean>>
  onInputFormChange: (e: ChangeEvent<HTMLInputElement>) => void
  openEmailBannerHelpPage: () => void
  handleCloseEmailBannerHelp: () => void
}

export type UseAntiPhishingLogic = [State, EventHandlers]

const SETTINGS_LIST = [
  AvailableSettings.BAFI,
  AvailableSettings.EXT_SENDER_WARNING,
  AvailableSettings.INTENT,
  AvailableSettings.REDIRECTOR_CONTENT_ACTION,
  AvailableSettings.LINKPROTECT,
  AvailableSettings.TYPOSQUAT,
  AvailableSettings.LINKED_DOMAIN_POLICY,
  AvailableSettings.EMAIL_BANNERS,
  AvailableSettings.EMAIL_BANNERS_TESTING_USERS
]

const initialForm: AntiPhishingSettings = {
  [AvailableSettings.BAFI]: BafiActions.BLOCK,
  [AvailableSettings.EXT_SENDER_WARNING]: SettingValue.DISABLED,
  [AvailableSettings.INTENT]: SettingValue.ENABLED,
  [AvailableSettings.REDIRECTOR_CONTENT_ACTION]: ContentIntentActions.DEFER,
  [AvailableSettings.INTENT_REALTIME]: SettingValue.DISABLED,
  [AvailableSettings.LINKPROTECT]: SettingValue.DISABLED,
  [AvailableSettings.TYPOSQUAT]: SettingValue.DISABLED,
  [AvailableSettings.EMAIL_BANNERS]: EmailBannersActions.OFF,
  [AvailableSettings.EMAIL_BANNERS_TESTING_USERS]: ''
}

const BASE_I18N_KEY = 'ess.inbound_settings.anti_phishing'

export const useAntiPhishingLogic = (): UseAntiPhishingLogic => {
  const isEmailBannerEnabled = isMyFeatureOn(FEATURES.EGD_Email_Banners_Rollout)
  const isBulkExemptionsOn = isMyFeatureOn(FEATURES.EGD_React_Additional_Exemptions)
  const dispatch = useAppDispatch()
  const formatMessage = useFormatMessage(BASE_I18N_KEY)
  const [form, setForm] = useState<AntiPhishingSettings>(initialForm)
  const [isHelpDialogOpened, setIsHelpDialogOpened] = useState<boolean>(false)
  const [showEmailBannersHelp, setShowEmailBannersHelp] = useState(false)
  const [error, setError] = useState('')

  const {
    accessTokenObject,
    isUpdateAccountSettingsSuccess,
    isUpdateDomainSettingsSuccess,
    isGetAccountSettingsSuccess,
    isGetDomainSettingsSuccess,
    accountSettings,
    domainSettings,
    isCplAccount
  } = useAppSelector(_stores => ({
    accessTokenObject: _stores.auth.accessTokenObject,
    isUpdateAccountSettingsSuccess: isSuccess(_stores.settings.updateAccountSettingsApiStatus),
    isUpdateDomainSettingsSuccess: isSuccess(_stores.settings.updateDomainSettingsApiStatus),
    isGetAccountSettingsSuccess: isSuccess(_stores.settings.getAccountSettingsApiStatus),
    isGetDomainSettingsSuccess: isSuccess(_stores.settings.getDomainSettingsApiStatus),
    accountSettings: _stores.settings.accountSettings,
    domainSettings: _stores.settings.domainSettings,
    isCplAccount: !!_stores.auth.accessTokenObject?.isCplAccount
  }))
  const { canQuarantineSettings, canChangeIntentRealtime } = useInboundSettingsRights()

  // Domain policy access check - only applies in domain-manage mode (when pdDomainId is set)
  const pdDomainId = accessTokenObject?.pdDomainId
  const isInDomainManageMode = !!pdDomainId
  const { hasNoDomainPolicyAccess: rawHasNoDomainPolicyAccess } = useDomainPolicyAccess(Number(pdDomainId) || 0)
  // Only block access when in domain-manage mode AND user has no R or U access
  const hasNoDomainPolicyAccess = isInDomainManageMode && rawHasNoDomainPolicyAccess

  const [{ isDirty, changedSettings }, resetInitialForm] = useDirtyFormObjectCheck(form)
  const [isAntiPhishingTableDirty, setIsAntiPhishingTableDirty] = useState<boolean>(false)
  const [isWarningBannerExemptTableDirty, setIsWarningBannerExemptTableDirty] = useState<boolean>(false)
  const [isAntifraudExemptTableDirty, setIsAntifraudExemptTableDirty] = useState<boolean>(false)

  // Build settings list for fetching
  const getSettingsList = useCallback(() => {
    const settingsList = canChangeIntentRealtime
      ? [...SETTINGS_LIST, AvailableSettings.INTENT_REALTIME]
      : [...SETTINGS_LIST]

    if (isBulkExemptionsOn) {
      if (isEmailBannerEnabled) {
        settingsList.push(AvailableSettings.EMAIL_BANNERS_SENDER_EXEMPT)
      }
      settingsList.push(AvailableSettings.BAFI_EXEMPT)
    }
    return settingsList
  }, [canChangeIntentRealtime, isBulkExemptionsOn, isEmailBannerEnabled])

  // init
  useEffect(() => {
    if (hasNoDomainPolicyAccess) {
      return
    }
    const settingsList = getSettingsList()
    if (accessTokenObject?.pdDomainId) {
      dispatch(getDomainSettings({ domainId: accessTokenObject?.pdDomainId, settings: settingsList }))
    } else {
      dispatch(getAccountSettings(settingsList))
    }
    // eslint-disable-next-line
  }, [hasNoDomainPolicyAccess])

  const onHelpClick = useCallback(() => {
    setIsHelpDialogOpened(true)
  }, [])

  const onCloseHelp = useCallback(() => {
    setIsHelpDialogOpened(false)
  }, [])

  const openEmailBannerHelpPage = useCallback(() => {
    setShowEmailBannersHelp(true)
    setIsHelpDialogOpened(true)
  }, [])

  const handleCloseEmailBannerHelp = useCallback(() => {
    setShowEmailBannersHelp(false)
    setIsHelpDialogOpened(false)
  }, [])

  useEffect(() => {
    if (isGetAccountSettingsSuccess || isGetDomainSettingsSuccess) {
      resetForm()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isGetAccountSettingsSuccess, isGetDomainSettingsSuccess])

  // update state on add/remove
  useEffect(() => {
    if (isUpdateAccountSettingsSuccess || isUpdateDomainSettingsSuccess) {
      const settingsList = getSettingsList()
      if (accessTokenObject?.pdDomainId) {
        dispatch(getDomainSettings({ domainId: accessTokenObject?.pdDomainId, settings: settingsList }))
      } else {
        dispatch(getAccountSettings(settingsList))
      }
    }
  }, [dispatch, accessTokenObject, isUpdateAccountSettingsSuccess, isUpdateDomainSettingsSuccess, getSettingsList])

  // unmount
  useEffect(
    () => () => {
      dispatch(resetAccountAndDomainSettings())
    },
    [dispatch]
  )

  const onFormChange = useCallback(
    (name: AvailableSettings) => (newValue: any) => {
      setForm({ ...form, [name]: newValue })
    },
    [form]
  )

  const resetForm = useCallback(() => {
    const settings = accessTokenObject?.pdDomainId ? domainSettings : accountSettings
    const nextForm = {
      [AvailableSettings.BAFI]: (settings.bafi as BafiActions) || initialForm.bafi,
      [AvailableSettings.EXT_SENDER_WARNING]:
        (settings.ext_sender_warning as SettingValue) || initialForm.ext_sender_warning,
      [AvailableSettings.INTENT]: (settings.intent as SettingValue) || initialForm.intent,
      [AvailableSettings.REDIRECTOR_CONTENT_ACTION]:
        (settings.redirector_content_action as ContentIntentActions) || initialForm.redirector_content_action,
      [AvailableSettings.INTENT_REALTIME]: (settings.intent_realtime as SettingValue) || initialForm.intent_realtime,
      [AvailableSettings.LINKPROTECT]: (settings.linkprotect as SettingValue) || initialForm.linkprotect,
      [AvailableSettings.TYPOSQUAT]: (settings.typosquat as SettingValue) || initialForm.typosquat,
      [AvailableSettings.EMAIL_BANNERS]: (settings.email_banners as EmailBannersActions) || initialForm.email_banners,
      [AvailableSettings.EMAIL_BANNERS_TESTING_USERS]:
        (settings.email_banners_testing_users as string) || initialForm.email_banners_testing_users
    }
    resetInitialForm(nextForm)
    setForm(nextForm)
  }, [accessTokenObject, domainSettings, accountSettings, resetInitialForm])

  const onBulkEdit = useCallback(() => {
    routesConfig.INBOUND_SETTINGS_INTENT_DOMAINS_BULK_EDIT.goto()
  }, [])

  const onSave = useCallback(() => {
    if (!isDirty) {
      return
    }

    if (form.email_banners === EmailBannersActions.TESTING_MODE) {
      if (!form.email_banners_testing_users) {
        setError(formatMessage('blank_email'))
        return
      }
      const emailArray = form.email_banners_testing_users.split(',').filter(email => !!email.trim())
      const invalidEmailList = emailArray.filter(email => !email || !isEmailValid(email))
      if (invalidEmailList.length > 0) {
        setError(formatMessage('invalid_email', { emails: invalidEmailList.join(', ') }))
        return
      }

      const uniqueEmails: Set<string> = new Set()
      const duplicates: string[] = []

      emailArray.forEach(email => {
        if (uniqueEmails.has(email)) {
          // Email is a duplicate
          duplicates.push(email)
        } else {
          // Email is unique, add it to the set
          uniqueEmails.add(email)
        }
      })
      if (duplicates.length > 0) {
        setError(formatMessage('duplicate_email', { emails: duplicates.join(', ') }))
        return
      }
    } else {
      delete changedSettings.email_banners_testing_users
    }

    // Trigger intercom event if previous saved values was On/testing mode
    const settings = accessTokenObject?.pdDomainId ? domainSettings : accountSettings
    if (isEmailBannerEnabled) {
      if (settings.email_banners !== SettingValue.DISABLED && changedSettings.email_banners === '0') {
        trackIntercomEventDelayed(TRACKING_EVENTS.WEBUI.EMAIL_BANNERS_TURNED_OFF)
      }
    }

    if (!canChangeIntentRealtime) {
      delete changedSettings.intent_realtime
    }

    // Force sending email_banners if changed settings include testing users
    const nextSettings = { ...changedSettings }
    if (Object.keys(changedSettings).includes(AvailableSettings.EMAIL_BANNERS_TESTING_USERS)) {
      nextSettings[AvailableSettings.EMAIL_BANNERS] = form[AvailableSettings.EMAIL_BANNERS]
    }

    if (accessTokenObject?.pdDomainId) {
      dispatch(
        updateDomainSettings({
          domainId: accessTokenObject.pdDomainId,
          settings: nextSettings
        })
      )
    } else {
      dispatch(updateAccountSettings({ settings: nextSettings }))
    }
    setError('')
    // Reset exemption table dirty states after save
    setIsAntiPhishingTableDirty(false)
    setIsWarningBannerExemptTableDirty(false)
    setIsAntifraudExemptTableDirty(false)
  }, [
    dispatch,
    form,
    accessTokenObject,
    domainSettings,
    accountSettings,
    isEmailBannerEnabled,
    canChangeIntentRealtime,
    formatMessage,
    changedSettings,
    isDirty
  ])

  const onCancelConfirm = useCallback(() => {
    resetForm()
  }, [resetForm])

  const onInputFormChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const { name, value } = e.target
      setForm({ ...form, [name]: value })
    },
    [form]
  )

  const bafiOptions = useMemo(() => {
    if (canQuarantineSettings) {
      return [
        {
          label: 'block',
          value: BafiActions.BLOCK
        },
        {
          label: 'quarantine',
          value: BafiActions.QUARANTINE
        },
        {
          label: 'off',
          value: BafiActions.OFF
        }
      ]
    }
    return [
      {
        label: 'block',
        value: BafiActions.BLOCK
      },
      {
        label: 'off',
        value: BafiActions.OFF
      }
    ]
  }, [canQuarantineSettings])

  const contentIntentActions = useMemo(
    () => (canQuarantineSettings ? AVAILABLE_CONTENT_INTENT_ACTIONS : AVAILABLE_CPL_CONTENT_INTENT_ACTIONS),
    [canQuarantineSettings]
  )

  return useMemo(
    () => [
      {
        canChangeIntentRealtime,
        bafiOptions,
        contentIntentActions,
        form,
        isDisabledSubmit: !isDirty,
        isDisabledNavigation:
          isDirty || isAntiPhishingTableDirty || isWarningBannerExemptTableDirty || isAntifraudExemptTableDirty,
        error,
        isEmailBannerEnabled: appConfig.APP.IS_ADMIN && !isCplAccount && isEmailBannerEnabled,
        showEmailBannersHelp,
        isDomainLevel: !!accessTokenObject?.pdDomainId,
        hasNoDomainPolicyAccess,
        isWarningBannerExemptionsOn:
          isBulkExemptionsOn &&
          isEmailBannerEnabled &&
          !isInDomainManageMode &&
          form.email_banners !== EmailBannersActions.OFF,
        isAntifraudExemptionsOn: isBulkExemptionsOn && !isInDomainManageMode && form.bafi !== BafiActions.OFF
      },
      {
        onFormChange,
        onSave,
        onCancelConfirm,
        onBulkEdit,
        helpConfig: {
          isOpen: isHelpDialogOpened,
          onHelpClick,
          onCloseHelp
        },
        setIsAntiPhishingTableDirty,
        setIsWarningBannerExemptTableDirty,
        setIsAntifraudExemptTableDirty,
        onInputFormChange,
        openEmailBannerHelpPage,
        handleCloseEmailBannerHelp
      }
    ],
    [
      canChangeIntentRealtime,
      bafiOptions,
      contentIntentActions,
      form,
      isDirty,
      isAntiPhishingTableDirty,
      isWarningBannerExemptTableDirty,
      isAntifraudExemptTableDirty,
      error,
      isCplAccount,
      isEmailBannerEnabled,
      isBulkExemptionsOn,
      showEmailBannersHelp,
      accessTokenObject?.pdDomainId,
      hasNoDomainPolicyAccess,
      isInDomainManageMode,
      onFormChange,
      onSave,
      onCancelConfirm,
      isHelpDialogOpened,
      onHelpClick,
      onCloseHelp,
      onInputFormChange,
      onBulkEdit,
      openEmailBannerHelpPage,
      handleCloseEmailBannerHelp
    ]
  )
}
